import React, { useState } from 'react';
import { FiCopy, FiTrendingUp, FiEdit, FiPlus, FiChevronRight } from 'react-icons/fi';
import ProgressionEditor from './ProgressionEditor';
import './CycleRepeater.css';

const CycleRepeater = ({
  program,
  setProgram,
  sourceCycleIndex,
  targetCycleIndex,
  onComplete,
  onSkip
}) => {
  const [repeatOption, setRepeatOption] = useState(null);
  const [showProgressionEditor, setShowProgressionEditor] = useState(false);

  const sourceCycle = program.cycles[sourceCycleIndex];

  // Safety check: if source cycle doesn't exist, show error
  if (!sourceCycle) {
    return (
      <div className="cycle-repeater">
        <div className="cycle-repeater-header">
          <h3>Error</h3>
          <p>Source cycle (Cycle {sourceCycleIndex + 1}) does not exist. Please create it first.</p>
        </div>
        <button className="action-btn secondary" onClick={onSkip}>
          Go Back
        </button>
      </div>
    );
  }

  const handleRepeatWithoutChange = () => {
    // Deep clone the source cycle
    const clonedCycle = JSON.parse(JSON.stringify(sourceCycle));
    clonedCycle.cycleNumber = targetCycleIndex + 1;

    setProgram(prev => {
      const newCycles = [...prev.cycles];
      newCycles[targetCycleIndex] = clonedCycle;
      return { ...prev, cycles: newCycles };
    });

    onComplete();
  };

  const handleRepeatWithProgression = () => {
    setShowProgressionEditor(true);
  };

  const applyProgression = (progressionSettings) => {
    // Clone the source cycle
    const clonedCycle = JSON.parse(JSON.stringify(sourceCycle));
    clonedCycle.cycleNumber = targetCycleIndex + 1;

    // Apply progression to each session's exercises
    clonedCycle.sessions = clonedCycle.sessions.map(session => ({
      ...session,
      blocks: session.blocks.map(block => ({
        ...block,
        exercises: block.exercises.map(exercise => {
          const exerciseId = exercise.exerciseId;
          const progression = progressionSettings[exerciseId] || progressionSettings.default;

          if (!progression) return exercise;

          const updatedMetrics = { ...exercise.primaryMetrics };

          // Apply weight progression
          if (progression.weightIncrease && updatedMetrics.weight) {
            const increase = progression.weightIncreaseType === 'percentage'
              ? updatedMetrics.weight * (progression.weightIncrease / 100)
              : progression.weightIncrease;
            updatedMetrics.weight = Math.round((updatedMetrics.weight + increase) * 2) / 2; // Round to nearest 0.5
          }

          // Apply set progression
          if (progression.setIncrease && updatedMetrics.sets) {
            updatedMetrics.sets = updatedMetrics.sets + progression.setIncrease;
          }

          // Apply rep progression
          if (progression.repIncrease && updatedMetrics.reps) {
            // Handle range reps like "8-12"
            if (typeof updatedMetrics.reps === 'string' && updatedMetrics.reps.includes('-')) {
              const [min, max] = updatedMetrics.reps.split('-').map(Number);
              updatedMetrics.reps = `${min + progression.repIncrease}-${max + progression.repIncrease}`;
            } else {
              const currentReps = parseInt(updatedMetrics.reps);
              updatedMetrics.reps = (currentReps + progression.repIncrease).toString();
            }
          }

          return { ...exercise, primaryMetrics: updatedMetrics };
        })
      }))
    }));

    setProgram(prev => {
      const newCycles = [...prev.cycles];
      newCycles[targetCycleIndex] = clonedCycle;
      return { ...prev, cycles: newCycles };
    });

    setShowProgressionEditor(false);
    onComplete();
  };

  const handleRepeatWithCustomEdits = () => {
    // Clone the source cycle
    const clonedCycle = JSON.parse(JSON.stringify(sourceCycle));
    clonedCycle.cycleNumber = targetCycleIndex + 1;

    setProgram(prev => {
      const newCycles = [...prev.cycles];
      newCycles[targetCycleIndex] = clonedCycle;
      return { ...prev, cycles: newCycles };
    });

    // User will manually edit sessions
    onComplete();
  };

  const handleFreshStart = () => {
    // Initialize empty cycle
    const newCycle = {
      cycleNumber: targetCycleIndex + 1,
      sessions: []
    };

    setProgram(prev => {
      const newCycles = [...prev.cycles];
      newCycles[targetCycleIndex] = newCycle;
      return { ...prev, cycles: newCycles };
    });

    onComplete();
  };

  if (showProgressionEditor) {
    return (
      <ProgressionEditor
        sourceCycle={sourceCycle}
        onApply={applyProgression}
        onCancel={() => setShowProgressionEditor(false)}
      />
    );
  }

  return (
    <div className="cycle-repeater">
      <div className="cycle-repeater-header">
        <h3>Cycle {targetCycleIndex + 1} Setup</h3>
        <p>How would you like to create Cycle {targetCycleIndex + 1} based on Cycle {sourceCycleIndex + 1}?</p>
      </div>

      <div className="repeat-options">
        {/* Option 1: Repeat without change */}
        <div
          className={`repeat-option ${repeatOption === 'same' ? 'selected' : ''}`}
          onClick={() => setRepeatOption('same')}
        >
          <div className="option-icon">
            <FiCopy />
          </div>
          <div className="option-content">
            <h4>Repeat Without Change</h4>
            <p>Clone Cycle {sourceCycleIndex + 1} exactly as-is</p>
          </div>
        </div>

        {/* Option 2: Repeat with linear progression */}
        <div
          className={`repeat-option ${repeatOption === 'progression' ? 'selected' : ''}`}
          onClick={() => setRepeatOption('progression')}
        >
          <div className="option-icon">
            <FiTrendingUp />
          </div>
          <div className="option-content">
            <h4>Repeat with Linear Progression</h4>
            <p>Apply weight/rep/set increases (customizable per exercise)</p>
          </div>
        </div>

        {/* Option 3: Repeat with custom edits */}
        <div
          className={`repeat-option ${repeatOption === 'custom' ? 'selected' : ''}`}
          onClick={() => setRepeatOption('custom')}
        >
          <div className="option-icon">
            <FiEdit />
          </div>
          <div className="option-content">
            <h4>Repeat + Custom Edits</h4>
            <p>Clone Cycle {sourceCycleIndex + 1} and manually customize sessions</p>
          </div>
        </div>

        {/* Option 4: Fresh start */}
        <div
          className={`repeat-option ${repeatOption === 'fresh' ? 'selected' : ''}`}
          onClick={() => setRepeatOption('fresh')}
        >
          <div className="option-icon">
            <FiPlus />
          </div>
          <div className="option-content">
            <h4>Completely Fresh</h4>
            <p>Create Cycle {targetCycleIndex + 1} from scratch with new sessions</p>
          </div>
        </div>
      </div>

      <div className="cycle-repeater-actions">
        <button className="skip-btn" onClick={onSkip}>
          Skip Cycle
        </button>
        <button
          className="continue-btn"
          disabled={!repeatOption}
          onClick={() => {
            switch (repeatOption) {
              case 'same':
                handleRepeatWithoutChange();
                break;
              case 'progression':
                handleRepeatWithProgression();
                break;
              case 'custom':
                handleRepeatWithCustomEdits();
                break;
              case 'fresh':
                handleFreshStart();
                break;
              default:
                break;
            }
          }}
        >
          Continue <FiChevronRight />
        </button>
      </div>
    </div>
  );
};

export default CycleRepeater;
